// src/components/vault/documents/id-validation/useProofOfAddressValidation.ts

import { useReducer, useCallback, useRef, useEffect } from "react";
import { PROGRESS_INTERVAL_MS, getLogarithmicProgress } from "./constants";
import {
  VerifyProofOfAddressResponse,
  ExtractedProofOfAddressData,
} from "../../../../redux/actions/verificationActions";
import { resolveScopedVaultApi } from "../../../../api/vault.api";
import { useVerifyProofOfAddress } from "../../../../hooks/vault/mutations/useVerifyProofOfAddress";
import {
  summarizeVerificationResponse,
  verificationDebugError,
  verificationDebugLog,
} from "../../../../utils/verificationDebug";
import { extractErrorMessage } from "../../../../utils/extractErrorMessage";

// Valid document types for proof of address
const VALID_PROOF_OF_ADDRESS_TYPES = [
  "water_bill",
  "electricity_bill",
  "predial",
  "communications_bill",
  "gas_bill",
  "phone_bill",
  "bank_statement",
] as const;

type ProofOfAddressDocumentType = (typeof VALID_PROOF_OF_ADDRESS_TYPES)[number];

const isValidProofOfAddressType = (
  type: string,
): type is ProofOfAddressDocumentType =>
  VALID_PROOF_OF_ADDRESS_TYPES.includes(type as ProofOfAddressDocumentType);

/**
 * Steps in the proof of address validation flow
 */
export type ProofOfAddressValidationStep =
  | "upload" // Initial state - user uploads document
  | "validating" // Loader showing during OCR
  | "data_edit" // Edit extracted address
  | "completed" // Flow finished
  | "pending_review" // Waiting for manual review
  | "rejected"; // Document rejected

/**
 * State of the proof of address validation flow
 */
export interface ProofOfAddressValidationState {
  currentStep: ProofOfAddressValidationStep;
  documentType: string | null;
  extractedAddress: ExtractedProofOfAddressData | null;
  validationProgress: number;
  error: string | null;
  invalidReason: string | null;
}

/**
 * Actions for the proof of address validation flow
 */
export type ProofOfAddressValidationAction =
  | { type: "START_VALIDATION"; documentType: string }
  | { type: "RESTORE_VALIDATING" }
  | { type: "UPDATE_PROGRESS"; progress: number }
  | {
      type: "VALIDATION_COMPLETE";
      extractedAddress: ExtractedProofOfAddressData | null;
    }
  | { type: "CONFIRM_ADDRESS" }
  | { type: "EDIT_ADDRESS" }
  | { type: "SAVE_EDITED_ADDRESS"; address: ExtractedProofOfAddressData }
  | { type: "SET_PENDING_REVIEW" }
  | { type: "SET_REJECTED"; invalidReason: string | null }
  | { type: "SET_COMPLETED" }
  | { type: "RESET_FLOW" }
  | { type: "VALIDATION_ERROR"; error: string };

/**
 * Initial state for the proof of address validation flow
 */
const INITIAL_STATE: ProofOfAddressValidationState = {
  currentStep: "upload",
  documentType: null,
  extractedAddress: null,
  validationProgress: 0,
  error: null,
  invalidReason: null,
};

/**
 * Reducer for the proof of address validation flow state machine
 */
function validationReducer(
  state: ProofOfAddressValidationState,
  action: ProofOfAddressValidationAction,
): ProofOfAddressValidationState {
  switch (action.type) {
    case "START_VALIDATION":
      return {
        ...state,
        currentStep: "validating",
        documentType: action.documentType,
        validationProgress: 0,
        error: null,
        invalidReason: null,
      };

    case "RESTORE_VALIDATING":
      return {
        ...state,
        currentStep: "validating",
        validationProgress: 0,
        error: null,
        invalidReason: null,
      };

    case "UPDATE_PROGRESS":
      return {
        ...state,
        validationProgress: action.progress,
      };

    case "VALIDATION_COMPLETE":
      return {
        ...state,
        currentStep: "completed",
        extractedAddress: action.extractedAddress,
        validationProgress: 100,
      };

    case "CONFIRM_ADDRESS":
      return {
        ...state,
        currentStep: "completed",
      };

    case "EDIT_ADDRESS":
      return {
        ...state,
        currentStep: "data_edit",
      };

    case "SAVE_EDITED_ADDRESS":
      return {
        ...state,
        currentStep: "completed",
        extractedAddress: action.address,
      };

    case "SET_PENDING_REVIEW":
      return {
        ...state,
        currentStep: "pending_review",
        validationProgress: 100,
        error: null,
        invalidReason: null,
      };

    case "SET_REJECTED":
      return {
        ...state,
        currentStep: "rejected",
        invalidReason: action.invalidReason,
        validationProgress: 0,
        error: null,
      };

    case "SET_COMPLETED":
      return {
        ...state,
        currentStep: "completed",
        validationProgress: 100,
        error: null,
        invalidReason: null,
      };

    case "RESET_FLOW":
      return INITIAL_STATE;

    case "VALIDATION_ERROR":
      return {
        ...state,
        currentStep: "upload",
        validationProgress: 0,
        error: action.error,
      };

    default:
      return state;
  }
}

/**
 * Hook to manage the proof of address validation flow
 *
 * Flow:
 * 1. User uploads document -> START_VALIDATION
 * 2. Call OCR API with progress animation -> UPDATE_PROGRESS
 * 3. API response determines next step:
 *    - valid: -> completed step (auto-upload)
 *    - pending_review: -> completed step (non-blocking, user advances)
 *    - invalid: -> rejected step (show error + re-upload)
 */
export function useProofOfAddressValidation() {
  const [state, dispatch] = useReducer(validationReducer, INITIAL_STATE);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const requirementIdRef = useRef<string | undefined>(undefined);
  const { mutateAsync: verifyProofOfAddress } = useVerifyProofOfAddress();
  const scopedVaultApi = resolveScopedVaultApi(
    typeof window !== "undefined" ? window.location.pathname : "",
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  /**
   * Persist workflow step to backend
   * Called when user navigates between steps to enable page reload recovery
   */
  const persistWorkflowStep = useCallback(
    async (step: string) => {
      if (requirementIdRef.current) {
        try {
          await scopedVaultApi.updateWorkflowStep(requirementIdRef.current, {
            workflow_step: step,
          });
        } catch (error) {
          verificationDebugError(
            "proofOfAddress.persistWorkflowStep.error",
            error,
            {
              requirementId: requirementIdRef.current,
              workflowStep: step,
            },
          );
          console.error("Failed to persist workflow step:", error);
        }
      }
    },
    [scopedVaultApi],
  );

  /**
   * Initialize flow state from requirement status AND workflow_step (for page reload detection)
   * Priority: discrepancy check > workflow_step > status
   */
  const initializeFromRequirement = useCallback(
    (requirement: {
      id?: string;
      status: string;
      invalid_reason?: string | null;
      workflow_step?: string | null;
      verification_data?: any;
    }) => {
      // Store requirement ID for future persistence
      requirementIdRef.current = requirement.id;
      verificationDebugLog(
        "proofOfAddress.restore",
        {
          requirementId: requirement.id,
          status: requirement.status,
          workflowStep: requirement.workflow_step,
        },
        requirement.verification_data,
      );

      // PRIORITY 0: Resolve discrepancy - status="valid" overrides workflow_step="pending_review"
      // This happens when an operator manually approves a pending_review requirement
      // and the backend didn't sync the workflow_step (or the data was cached)
      if (
        requirement.status === "valid" &&
        requirement.workflow_step === "pending_review"
      ) {
        dispatch({ type: "SET_COMPLETED" });
        return;
      }

      // PRIORITY 1: Use workflow_step if available (most precise state)
      // pending_review is no longer blocking - treat it as completed
      if (requirement.workflow_step) {
        switch (requirement.workflow_step) {
          case "validating":
            dispatch({ type: "RESTORE_VALIDATING" });
            return;
          case "pending_review":
          case "completed":
            dispatch({ type: "SET_COMPLETED" });
            return;
          case "rejected":
            dispatch({
              type: "SET_REJECTED",
              invalidReason: requirement.invalid_reason ?? null,
            });
            return;
          default:
            // Fall through to status-based logic
            break;
        }
      }

      // PRIORITY 2: Fallback to status-based initialization (existing logic)
      // pending_review is no longer blocking - treat it as completed
      switch (requirement.status) {
        case "pending_review":
        case "valid":
          dispatch({ type: "SET_COMPLETED" });
          break;
        case "invalid":
          dispatch({
            type: "SET_REJECTED",
            invalidReason: requirement.invalid_reason ?? null,
          });
          break;
        default:
          // pending_upload or unknown -> show upload form
          dispatch({ type: "RESET_FLOW" });
      }
    },
    [],
  );

  /**
   * Start the validation process
   * Calls real OCR API and shows progress animation during the request
   */
  const startValidation = useCallback(
    async (
      documentType: string,
      imageFile: File,
      requirementId?: string,
    ): Promise<VerifyProofOfAddressResponse | void> => {
      // Validate document type at runtime before proceeding
      if (!isValidProofOfAddressType(documentType)) {
        throw new Error(`Tipo de comprobante invalido: ${documentType}`);
      }

      // Store requirement ID for future persistence
      requirementIdRef.current = requirementId;
      verificationDebugLog("proofOfAddress.start", {
        documentType,
        requirementId,
        imageFile: {
          name: imageFile.name,
          size: imageFile.size,
          type: imageFile.type,
        },
      });

      dispatch({ type: "START_VALIDATION", documentType });

      startTimeRef.current = Date.now();

      // Clear any existing timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Start progress animation with logarithmic curve (fast start, gradual slowdown)
      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        const progress = getLogarithmicProgress(elapsed);
        dispatch({ type: "UPDATE_PROGRESS", progress });
      }, PROGRESS_INTERVAL_MS);

      try {
        // Call real API
        const formData = new FormData();
        formData.append("document_type", documentType);
        formData.append("image", imageFile);
        if (requirementId) formData.append("requirement_id", requirementId);

        const response = (await verifyProofOfAddress(
          formData,
        )) as VerifyProofOfAddressResponse;
        verificationDebugLog(
          "proofOfAddress.response",
          {
            documentType,
            requirementId,
            response: summarizeVerificationResponse(response),
          },
          response,
        );

        // Clear progress interval
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        // Set progress to 100%
        dispatch({ type: "UPDATE_PROGRESS", progress: 100 });

        // Small delay for UX (show 100% completion)
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Handle response based on status
        // pending_review is no longer blocking - treat it like valid so user advances
        switch (response.status) {
          case "valid":
          case "pending_review":
            // Both valid and pending_review allow user to continue
            verificationDebugLog("proofOfAddress.decision", {
              documentType,
              requirementId,
              status: response.status,
              nextStep: "completed",
              invalidReason: response.invalid_reason,
            });
            dispatch({ type: "CONFIRM_ADDRESS" });
            persistWorkflowStep("completed");
            break;

          case "invalid":
            verificationDebugLog("proofOfAddress.decision", {
              documentType,
              requirementId,
              status: response.status,
              nextStep: "rejected",
              invalidReason: response.invalid_reason,
            });
            dispatch({
              type: "SET_REJECTED",
              invalidReason: response.invalid_reason,
            });
            persistWorkflowStep("rejected");
            break;

          default:
            // Fallback - treat unknown status as error so the user is not
            // stuck on the validating screen
            dispatch({
              type: "VALIDATION_ERROR",
              error: `Estado de validación desconocido: ${response.status}`,
            });
        }

        return response;
      } catch (error: any) {
        // Clear progress interval
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        // Dispatch error action with error message
        const errorMessage = extractErrorMessage(
          error,
          "Error al verificar el comprobante",
        );
        verificationDebugError("proofOfAddress.error", error, {
          documentType,
          requirementId,
          errorMessage,
        });
        dispatch({ type: "VALIDATION_ERROR", error: errorMessage });
        // Don't re-throw - let state handle the error display
      }
    },
    [persistWorkflowStep, verifyProofOfAddress],
  );

  /**
   * Confirm that the extracted address is correct
   * Persists workflow_step="uploading" to signal that upload is in progress
   */
  const confirmAddress = useCallback(() => {
    dispatch({ type: "CONFIRM_ADDRESS" });
    // Persist "completed" since the upload will happen immediately after
    persistWorkflowStep("completed");
  }, [persistWorkflowStep]);

  /**
   * Request to edit the extracted address
   */
  const editAddress = useCallback(() => {
    dispatch({ type: "EDIT_ADDRESS" });
  }, []);

  /**
   * Save edited address and complete the flow
   */
  const saveEditedAddress = useCallback(
    (address: ExtractedProofOfAddressData) => {
      dispatch({ type: "SAVE_EDITED_ADDRESS", address });
      persistWorkflowStep("completed");
    },
    [persistWorkflowStep],
  );

  /**
   * Reset the flow to initial state
   */
  const resetFlow = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    dispatch({ type: "RESET_FLOW" });
  }, []);

  /**
   * Cancel the validation (e.g., when navigating away)
   */
  const cancelValidation = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    dispatch({ type: "RESET_FLOW" });
  }, []);

  return {
    state,
    // Actions
    startValidation,
    initializeFromRequirement,
    confirmAddress,
    editAddress,
    saveEditedAddress,
    resetFlow,
    cancelValidation,
    // Computed values for convenience
    isValidating: state.currentStep === "validating",
    isCompleted: state.currentStep === "completed",
    isPendingReview: state.currentStep === "pending_review",
    isRejected: state.currentStep === "rejected",
    showDataEdit: state.currentStep === "data_edit",
    hasError: state.error !== null,
    error: state.error,
    invalidReason: state.invalidReason,
    progress: state.validationProgress,
    // Legacy API compatibility (for simple loader usage)
    isComplete: state.currentStep === "completed",
  };
}

export type UseProofOfAddressValidationReturn = ReturnType<
  typeof useProofOfAddressValidation
>;
